import React, { useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Tabs,
  Typography,
} from 'antd';
import { EditOutlined, PlusOutlined, ShoppingCartOutlined, SwapOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { OrderOptionDto } from '../dto/order';
import type { TransportRouteDetailDto } from '../dto/transport';
import { isPreConfirmationOrderStatus } from '../modules/orders/orderStatusTransitions';
import TransportCargoItemCard from './TransportCargoItemCard';
import TransportFormSection from './TransportFormSection';
import TransportOrderPickerField from './TransportOrderPickerField';
import TransportOrderLoadView from './TransportOrderLoadView';
import type { CargoTableRow } from './sharedTransportFormRows';
import {
  findStaleCargoQuantityWarnings,
  formatStaleCargoQuantityWarningItems,
} from './sharedTransportFormUtils';
import {
  type AllocationMethod as CostAllocationMethod,
  type TransportCostAllocationItemResult,
} from '../utils/transportCostAllocation';
import type { TransportKind } from '../modules/transports/transportDirection';
import './TransportOrdersCargoSection.css';

const { Text } = Typography;

interface CargoStatItem {
  key: string;
  label: string;
  value: string;
}

interface TransportOrdersCargoSectionProps {
  transport?: TransportRouteDetailDto | null;
  /**
   * Etap 2 (transport przywozowy): the inbound supplier-order (ZD) linking
   * field, rendered above the customer-order picker. Passed in as a node so
   * this section stays presentational and the selection state keeps living in
   * SharedTransportForm next to the payload builder.
  */
  supplierOrdersField?: React.ReactNode;
  /**
   * Selected ZDs rendered inline in the section (outside the picker modal).
   * Same ownership rule as `supplierOrdersField`.
   */
  supplierOrdersSelectedList?: React.ReactNode;
  /**
   * Addendum A: „Towar z ZD na tym transporcie” — how much of each ZD line
   * this INBOUND truck carries. State lives in SharedTransportForm.
   */
  supplierCargoEditor?: React.ReactNode;
  /** 2026-10-10: the ZD load view (replaces the selected-ZD list on factory trucks). */
  supplierLoadView?: React.ReactNode;
  selectedSupplierOrderCount?: number;
  /**
   * Decision 2026-10-04: the truck kind decides which orders it carries —
   * „Fabryka → magazyn” has no customer orders (picker and cargo hidden).
   */
  customerOrdersEnabled?: boolean;
  /** Round 2 (U9): manual amounts live in the split table. */
  manualInSplitTable?: boolean;
  /** Usability round 2026-10-05: picker texts name what this kind carries. */
  kind?: TransportKind | null;
  canEditTransportCosts: boolean;
  /** Customer sale value of the goods; without it the field is hidden, not 0. */
  canViewGoodsValue?: boolean;
  availableOrders: OrderOptionDto[];
  availableColumns: ColumnsType<OrderOptionDto>;
  orderRowSelection: {
    selectedRowKeys: string[];
    onChange: (keys: React.Key[]) => void;
  };
  scopedOrder?: OrderOptionDto;
  /**
   * Decyzja 2026-09-14: informacja o punkcie startu („Transport dla
   * zamówienia"), nie blokada. Picker zamówień klientów jest zawsze pełny —
   * operator może dołożyć kolejne zamówienia do tej ciężarówki.
   */
  orderScopeActive?: boolean;
  /**
   * Owner 2026-10-14: ZK/ZD join a truck only from their own cards, so the
   * edit form has no picker (false); the create form, opened from a ZK/ZD
   * card, keeps it.
   */
  canPickOrders?: boolean;
  cargoRows: CargoTableRow[];
  allocationHintsLoading?: boolean;
  allocationHintsError?: boolean;
  missingCargoOrderNumbers?: string[];
  currency?: string;
  costAllocationMethod: CostAllocationMethod;
  showUnitCosts: boolean;
  onShowUnitCostsChange: (checked: boolean) => void;
  allocationItemsById: Map<string | undefined, TransportCostAllocationItemResult>;
  formatCurrencyAmount: (value: number | null | undefined) => string;
  onQuantityTouched?: (key: string) => void;
  onRemoveCargo: (key: string) => void;
  cargoStatItems: CargoStatItem[];
  hasMissingDimensions: boolean;
  /** Delivered-quantity confirmation fields (status DELIVERED), same panel. */
  deliveryQuantities?: React.ReactNode;
  /** Our warehouse (GET /transport-routes/defaults): the fixed loading / unloading place. */
  warehouseAddress?: string | null;
  /** PR3: „z pozycji ZD” of each ZK cargo line (factory → customer truck only). */
  renderCargoPairing?: (record: CargoTableRow) => React.ReactNode;
  /** 2026-10-09: „Dla kogo jest ten towar?” on a factory truck (rows = ZD lines). */
  recipientsPanel?: React.ReactNode;
  /**
   * Owner 2026-10-11: the ZK quantities window („Zmień ilości na tym aucie”);
   * the form opens it when a save is refused on a field inside it.
   */
  cargoModalOpen?: boolean;
  onCargoModalOpenChange?: (open: boolean) => void;
}

const TransportOrdersCargoSection: React.FC<TransportOrdersCargoSectionProps> = ({
  transport,
  supplierOrdersField,
  supplierOrdersSelectedList,
  supplierCargoEditor = null,
  supplierLoadView = null,
  selectedSupplierOrderCount = 0,
  customerOrdersEnabled = true,
  kind = null,
  manualInSplitTable = false,
  canEditTransportCosts,
  canViewGoodsValue = true,
  availableOrders,
  availableColumns,
  orderRowSelection,
  scopedOrder,
  orderScopeActive = false,
  canPickOrders = true,
  cargoRows,
  allocationHintsLoading = false,
  allocationHintsError = false,
  missingCargoOrderNumbers = [],
  currency,
  costAllocationMethod,
  showUnitCosts,
  onShowUnitCostsChange,
  allocationItemsById,
  formatCurrencyAmount,
  onQuantityTouched = () => undefined,
  onRemoveCargo,
  cargoStatItems,
  hasMissingDimensions,
  deliveryQuantities,
  warehouseAddress = null,
  renderCargoPairing,
  recipientsPanel = null,
  cargoModalOpen,
  onCargoModalOpenChange,
}) => {
  const { t } = useTranslation(['transport', 'common']);
  // Operator feedback (2026-09, "jak najmniej scrollowania"): the order
  // pickers live in a modal; the section itself shows only what is on the
  // truck plus one button.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [ownCargoModalOpen, setOwnCargoModalOpen] = useState(false);
  const quantitiesOpen = cargoModalOpen ?? ownCargoModalOpen;
  const setQuantitiesOpen = onCargoModalOpenChange ?? setOwnCargoModalOpen;

  // Owner-approved: transport for an unconfirmed order stays fully allowed —
  // no role/status gate, no save blocking — but the form shows a non-blocking
  // informational warning so the operator double-checks the customer actually
  // confirmed. isPreConfirmationOrderStatus is the single home for the status
  // set (NEW_INQUIRY/QUOTE_SENT). Uses the same displayOrderNumber the order
  // picker's own column renders, never the raw id.
  const selectedRowKeySet = new Set(orderRowSelection.selectedRowKeys);
  const unconfirmedSelectedOrderNumbers = availableOrders
    .filter((order) => selectedRowKeySet.has(order.id) && isPreConfirmationOrderStatus(order.status))
    .map((order) => order.displayOrderNumber || order.orderNumber);

  // HOTEL (HIGH, "warn, never block"): a persisted cargo line's loaded
  // quantity can exceed the order product's CURRENT quantity when the order
  // was edited down after this transport was built. Edit-mode only
  // (`transport` is only set once editing an existing transport). Non-blocking
  // — the existing per-line overflow guard (which blocks save, further down in
  // this component) is a separate, unrelated check and stays exactly as-is.
  const staleQuantityWarnings = transport ? findStaleCargoQuantityWarnings(transport) : [];

  const hasAnySelection = orderRowSelection.selectedRowKeys.length > 0 || selectedSupplierOrderCount > 0;
  // Cargo lines come from customer orders only; an inbound-only truck has none.
  const showCargoBlock = customerOrdersEnabled
    && (orderRowSelection.selectedRowKeys.length > 0 || orderScopeActive || cargoRows.length > 0);
  // PR3 (decision 2026-10-06): the truck is „Załadunek” → „Rozładunek”; the
  // kind says which side is our warehouse (read-only) and which takes orders.
  const loadsAtWarehouse = kind === 'WAREHOUSE_TO_CUSTOMER';
  const unloadsAtWarehouse = kind === 'FACTORY_TO_WAREHOUSE';
  // The cargo editor is always passed; only a selected ZD puts goods on the loading side.
  const hasSupplierLoad = selectedSupplierOrderCount > 0;
  const showLegs = hasAnySelection || showCargoBlock || Boolean(supplierCargoEditor)
    || loadsAtWarehouse || unloadsAtWarehouse;
  const warehouseCard = (
    <div className="transport-leg__warehouse" data-testid="transport-leg-warehouse">
      <Text strong>{t('transport:legs.warehouse')}</Text>
      <Text type="secondary">{warehouseAddress || t('transport:legs.warehouseNoAddress')}</Text>
    </div>
  );
  // Owner 2026-10-10: on a factory truck „Gdzie jedzie” (the drop board)
  // owns customers and quantities; the per-line cards stay one click away.
  const boardActive = Boolean(hasSupplierLoad && recipientsPanel);
  const wrapCustomerDetails = (children: React.ReactNode) => (boardActive && (orderRowSelection.selectedRowKeys.length > 0 || cargoRows.length > 0) ? (
    <details className="transport-leg__details" data-testid="transport-customer-details">
      <summary>{t('transport:dropBoard.details')}</summary>
      {children}
    </details>
  ) : <>{children}</>);
  const pickerButtonLabel = t(kind
    ? `transport:orderPicker.byKind.${kind}.${hasAnySelection ? 'change' : 'open'}`
    : (hasAnySelection ? 'transport:orderPicker.changeButton' : 'transport:orderPicker.openButton'));
  const emptyLeg = (hintKey: 'emptyLoading' | 'emptyUnloading') => (
    <Text type="secondary" data-testid={`transport-leg-${hintKey}`}>
      {canPickOrders
        ? t(`transport:legs.${hintKey}`, { button: pickerButtonLabel })
        : t('transport:orderPicker.addFromCards')}
    </Text>
  );
  const summaryParts = [
    orderRowSelection.selectedRowKeys.length > 0
      ? t('transport:orderPicker.selectedCount', { count: orderRowSelection.selectedRowKeys.length })
      : null,
    selectedSupplierOrderCount > 0
      ? t('transport:orderPicker.selectedSupplierCount', { count: selectedSupplierOrderCount })
      : null,
  ].filter(Boolean);

  return (
    <TransportFormSection
      sectionKey="orders"
      step={1}
      icon={<ShoppingCartOutlined />}
      label={t('transport:sections.ordersCargo')}
      summary={summaryParts.length > 0 ? summaryParts.join(' · ') : undefined}
      className="transport-orders-cargo-card"
    >
      {orderScopeActive ? (
        <div className="transport-order-scope" data-testid="transport-order-scope">
          {scopedOrder ? (
            <>
              <div className="transport-order-scope__identity">
                <Text type="secondary" className="transport-order-scope__eyebrow">
                  {t('transport:scopedOrder.label')}
                </Text>
                <Text strong className="transport-order-scope__number">
                  {scopedOrder.displayOrderNumber || scopedOrder.orderNumber}
                </Text>
                <Text className="transport-order-scope__customer">
                  {scopedOrder.customer?.name || scopedOrder.customerName || '—'}
                </Text>
              </div>
              <Text type="secondary" className="transport-order-scope__hint">
                {t('transport:scopedOrder.hint')}
              </Text>
            </>
          ) : (
            <Text type="secondary">{t('transport:scopedOrder.loading')}</Text>
          )}
        </div>
      ) : null}

      {canPickOrders ? (
        <>
          <div className={`transport-orders-picker-launch${hasAnySelection ? '' : ' transport-orders-picker-launch--empty'}`}>
            {!hasAnySelection ? (
              <Text type="secondary">{t(kind ? `transport:orderPicker.byKind.${kind}.empty` : 'transport:orderPicker.emptySelection')}</Text>
            ) : null}
            <Button
              type={hasAnySelection ? 'default' : 'primary'}
              icon={hasAnySelection ? <SwapOutlined /> : <PlusOutlined />}
              onClick={() => setPickerOpen(true)}
              data-testid="transport-order-picker-open"
              data-transport-order-picker-open="true"
            >
              {pickerButtonLabel}
            </Button>
          </div>

          <Modal
            open={pickerOpen}
            title={t('transport:orderPicker.modalTitle')}
            width={920}
            destroyOnHidden
            onCancel={() => setPickerOpen(false)}
            footer={(
              <Button type="primary" onClick={() => setPickerOpen(false)} data-testid="transport-order-picker-done">
                {t('transport:orderPicker.done')}
              </Button>
            )}
          >
            {supplierOrdersField && !customerOrdersEnabled ? supplierOrdersField : supplierOrdersField ? (
              <Tabs
                defaultActiveKey="customer"
                items={[
                  {
                    key: 'customer',
                    label: t('transport:orderPicker.customerTab'),
                    children: (
                      <TransportOrderPickerField
                        availableOrders={availableOrders}
                        availableColumns={availableColumns}
                        orderRowSelection={orderRowSelection}
                      />
                    ),
                  },
                  {
                    key: 'supplier',
                    label: t('transport:orderPicker.supplierTab'),
                    children: supplierOrdersField,
                  },
                ]}
              />
            ) : (
              <TransportOrderPickerField
                availableOrders={availableOrders}
                availableColumns={availableColumns}
                orderRowSelection={orderRowSelection}
              />
            )}
          </Modal>
        </>
      ) : (
        <Text type="secondary" className="transport-orders-picker-locked" data-testid="transport-order-picker-locked">
          {t('transport:orderPicker.addFromCards')}
        </Text>
      )}

      {showLegs ? (
        <div className="transport-orders-selection transport-legs" data-testid="transport-orders-selection">
          <section className="transport-leg" data-testid="transport-leg-loading">
            <h4 className="transport-leg__title">{t('transport:legs.loading')}</h4>
            {loadsAtWarehouse ? warehouseCard : hasSupplierLoad ? (
              <>
                {supplierLoadView ?? supplierOrdersSelectedList}
                {supplierCargoEditor}
              </>
            ) : emptyLeg('emptyLoading')}
          </section>

          {/* Owner 2026-10-10: on a factory truck unloading lives in section 2
              „Gdzie jedzie”; here only the folded customer cargo details stay. */}
          {boardActive && orderRowSelection.selectedRowKeys.length === 0 && cargoRows.length === 0 ? null : (
          <section className={`transport-leg${boardActive ? ' transport-leg--details-only' : ''}`} data-testid="transport-leg-unloading">
            {boardActive ? null : <h4 className="transport-leg__title">{t('transport:legs.unloading')}</h4>}
            {unloadsAtWarehouse && !(hasSupplierLoad && recipientsPanel) ? warehouseCard : null}
            {!unloadsAtWarehouse && !(hasSupplierLoad && recipientsPanel) && orderRowSelection.selectedRowKeys.length === 0 && !showCargoBlock ? emptyLeg('emptyUnloading') : null}
            {wrapCustomerDetails(<>
            {!unloadsAtWarehouse ? (
              <TransportOrderLoadView
                orders={orderRowSelection.selectedRowKeys
                  .map((id) => availableOrders.find((order) => order.id === id))
                  .filter((order): order is OrderOptionDto => Boolean(order))}
                rows={cargoRows}
                onRemoveOrder={(orderId) => orderRowSelection.onChange(
                  orderRowSelection.selectedRowKeys.filter((key) => key !== orderId),
                )}
              />
            ) : null}

            {unconfirmedSelectedOrderNumbers.length > 0 ? (
              <Alert
                type="warning"
                showIcon
                message={unconfirmedSelectedOrderNumbers.length === 1
                  ? t('transport:cargo.unconfirmedOrderWarningSingular', { number: unconfirmedSelectedOrderNumbers[0] })
                  : t('transport:cargo.unconfirmedOrderWarningPlural', { numbers: unconfirmedSelectedOrderNumbers.join(', ') })}
                style={{ marginTop: 16 }}
              />
            ) : null}

            {/* HOTEL (HIGH, "warn, never block"): the order changed since this
                transport's cargo was built (e.g. quantity reduced) — non-blocking,
                no auto-resync, save is NOT blocked by this Alert. */}
            {staleQuantityWarnings.length > 0 ? (
              <Alert
                type="warning"
                showIcon
                message={t('transport:cargo.staleQuantityWarningTitle')}
                description={(
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {formatStaleCargoQuantityWarningItems(staleQuantityWarnings, t).map((line, index) => (
                      <li key={staleQuantityWarnings[index].orderProductId}>{line}</li>
                    ))}
                  </ul>
                )}
                style={{ marginTop: 16 }}
              />
            ) : null}

            {allocationHintsLoading ? (
              <Alert
                type="info"
                showIcon
                message={t('transport:cargo.availabilityLoading')}
                style={{ marginTop: 16 }}
              />
            ) : null}

            {allocationHintsError ? (
              <Alert
                type="error"
                showIcon
                message={t('transport:cargo.availabilityLoadError')}
                style={{ marginTop: 16 }}
              />
            ) : null}

            {!allocationHintsLoading && missingCargoOrderNumbers.length > 0 ? (
              <Alert
                type="error"
                showIcon
                message={t('transport:cargo.orderWithoutCargoBlocked', {
                  numbers: missingCargoOrderNumbers.join(', '),
                })}
                style={{ marginTop: 16 }}
              />
            ) : null}

            {/* Nothing selected yet: the launch box above is the whole story; the
                cargo divider, empty state and zero statistics would only add noise. */}
            {showCargoBlock ? (
            <>
            {cargoRows.length === 0 ? (
              // Friendly empty state instead of a bare table with a validation
              // sentence — no orders selected yet, nothing to load.
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('transport:cargo.emptyStateHint')}
                style={{ margin: '24px 0' }}
              />
            ) : (
              <>
                {/* Owner 2026-10-11: like the ZD load — the view above, quantities in a window. */}
                <Button
                  icon={<EditOutlined />}
                  onClick={() => setQuantitiesOpen(true)}
                  data-testid="transport-cargo-edit-open"
                  style={{ justifySelf: 'start', marginTop: 8 }}
                >
                  {t('transport:supplierCargo.editButton')}
                </Button>
                <Modal
                  open={quantitiesOpen}
                  forceRender
                  width={980}
                  title={t('transport:orderLoad.editTitle')}
                  onCancel={() => setQuantitiesOpen(false)}
                  footer={(
                    <Button type="primary" onClick={() => setQuantitiesOpen(false)} data-testid="transport-cargo-edit-done">
                      {t('transport:supplierCargo.editDone')}
                    </Button>
                  )}
                >
                {canEditTransportCosts ? (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <Space size={8}>
                      <Switch
                        id="transport-unit-costs-toggle"
                        checked={showUnitCosts}
                        onChange={onShowUnitCostsChange}
                        size="small"
                        aria-label={t('transport:unitCostsToggle')}
                      />
                      <label htmlFor="transport-unit-costs-toggle">{t('transport:unitCostsToggle')}</label>
                    </Space>
                  </div>
                ) : null}
                <div
                  data-testid="transport-cargo-list"
                  style={{ display: 'grid', gap: 12, minWidth: 0, width: '100%' }}
                >
                  {cargoRows.map((record) => (
                    <TransportCargoItemCard
                      key={record.key}
                      record={record}
                      canEditTransportCosts={canEditTransportCosts}
                      canViewGoodsValue={canViewGoodsValue}
                      currency={currency}
                      costAllocationMethod={costAllocationMethod}
                      showUnitCosts={showUnitCosts}
                      allocationItem={allocationItemsById.get(record.id || record.key)}
                      formatCurrencyAmount={formatCurrencyAmount}
                      onQuantityTouched={onQuantityTouched}
                      onRemoveCargo={onRemoveCargo}
                      manualInSplitTable={manualInSplitTable}
                      pairingField={renderCargoPairing ? renderCargoPairing(record) : null}
                    />
                  ))}
                </div>
                </Modal>
              </>
            )}

            {/* Owner 2026-10-11: one thin summary line; the trailer above shows the weight. */}
            <div className="transport-cargo-summary" data-testid="transport-cargo-summary">
              {cargoStatItems.filter((item) => item.key !== 'weight').map((item) => (
                <span key={item.key}>
                  <Text type="secondary">{item.label}:</Text> {item.value}
                </span>
              ))}
              {hasMissingDimensions ? (
                <Text type="warning">{t('transport:cargo.missingDimensionsHint')}</Text>
              ) : null}
            </div>
            </>
            ) : null}
            </>)}
          </section>
          )}
        </div>
      ) : null}

      {/* Hidden-but-registered auto-computed fields — the payload keeps them,
          so the read-only stat row above is purely presentational. */}
      <Form.Item name="totalWeight" hidden noStyle>
        <InputNumber />
      </Form.Item>
      <Form.Item name="totalVolume" hidden noStyle>
        <InputNumber />
      </Form.Item>
      <Form.Item name="dimensions" hidden noStyle>
        <Input />
      </Form.Item>

      {deliveryQuantities}
    </TransportFormSection>
  );
};

export default TransportOrdersCargoSection;
